import React from 'react';
import { Search, Loader2, X, Plus, Check } from 'lucide-react';
import Button from '../Button/Button';
import { DropdownItem } from './SearchableDropdown';

interface DropdownContentProps {
  items: DropdownItem[];
  selectedItem?: DropdownItem | null;
  search: string;
  setSearch: (search: string) => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
  searchPlaceholder: string;
  loading: boolean;
  emptyText: string;
  allowCustomValue: boolean;
  shouldShowAddNew: boolean;
  handleSelect: (item: DropdownItem) => void;
  handleListScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  handleCreateCustomValue: () => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  hasMore: boolean;
}

function DropdownContent({
  items,
  selectedItem,
  search,
  setSearch,
  searchInputRef,
  searchPlaceholder,
  loading,
  emptyText,
  allowCustomValue,
  shouldShowAddNew,
  handleSelect,
  handleListScroll,
  handleCreateCustomValue,
  handleKeyDown,
  hasMore
}: Readonly<DropdownContentProps>): React.ReactElement {
  return (
    <>
      <div className='flex items-center border-b px-4 py-3'>
        <Search size={16} className='text-gray-400 mr-2 flex-shrink-0' />
        <input
          ref={searchInputRef}
          type='text'
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          className='flex-1 text-base bg-transparent outline-none text-gray-900 placeholder:text-gray-400'
        />
        {search && (
          <button
            type='button'
            onClick={() => setSearch('')}
            className='ml-2 p-1 text-gray-400 hover:text-gray-600 flex-shrink-0'
            aria-label='Clear search'
            title='Clear search'
          >
            <X size={16} />
          </button>
        )}
      </div>

      {((): React.ReactElement => {
        if (loading && items.length === 0) {
          return (
            <div className='flex justify-center items-center py-4'>
              <Loader2 size={20} className='animate-spin text-gray-400' />
            </div>
          );
        }

        if (items.length === 0 && !shouldShowAddNew) {
          return (
            <div className='flex justify-center items-center py-4 text-gray-500 text-sm'>
              {emptyText}
            </div>
          );
        }

        return (
          <div
            className='max-h-60 overflow-y-auto'
            onScroll={handleListScroll}
            onWheel={(e) => e.stopPropagation()}
          >
            {shouldShowAddNew && (
              <Button
                type='button'
                variant='ghost'
                // eslint-disable-next-line max-len
                className='flex items-center justify-between px-3 py-2 bg-blue-50 hover:bg-blue-100 w-full text-left h-auto text-base font-normal rounded-none border-b border-blue-200'
                onClick={handleCreateCustomValue}
              >
                <div className='flex items-center flex-1 min-w-0'>
                  <div className='w-6 flex justify-center mr-2'>
                    <Plus size={16} className='text-blue-600' />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='text-base text-blue-700 truncate'>
                      Add &quot;{search}&quot;
                    </div>
                    <div className='text-xs text-blue-500 truncate'>
                      Press Enter to create new topic
                    </div>
                  </div>
                </div>
              </Button>
            )}

            {items.map((item) => (
              <Button
                key={item.id}
                type='button'
                variant='ghost'
                className='flex items-center justify-between px-3 py-2 bg-white hover:bg-gray-50 w-full text-left h-auto text-base font-normal rounded-none'
                onClick={() => handleSelect(item)}
              >
                <div className='flex items-center flex-1 min-w-0'>
                  {selectedItem?.id === item.id && (
                    <div className='w-6 flex justify-center mr-2'>
                      <Check size={16} className='text-green-600' />
                    </div>
                  )}
                  <div className='flex-1 min-w-0'>
                    <div className='text-base text-gray-900 truncate'>
                      {item.label}
                    </div>
                    {item.sublabel && (
                      <div className='text-xs text-gray-400 truncate'>
                        {item.sublabel}
                      </div>
                    )}
                  </div>
                </div>
              </Button>
            ))}

            {loading && hasMore && (
              <div className='flex justify-center items-center py-2'>
                <Loader2 size={16} className='animate-spin text-gray-400' />
              </div>
            )}
          </div>
        );
      })()}
    </>
  );
}

export default DropdownContent;
